import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, Circle, ClipboardSignature, PenLine, Upload } from 'lucide-react';
import { PatientDocumentsService } from '../../../services/patientmr/patientDocuments.service';
import { LegalGuardiansService } from '../../../services/patientmr/legalGuardians.service';
import { notifyPatientChartUpdated } from '../../../constants/navigationEvents';
import { useAuth } from '../../../context/authcontext';
import { PATIENT_DOCUMENT_TYPE_LABELS } from '../../../dtos/patientmr/patientDocuments.dto';
import type { AdmissionPacket, AdmissionPacketItem, PatientDocument } from '../../../dtos/patientmr/patientDocuments.dto';
import type { LegalGuardian } from '../../../dtos/patientmr/legalGuardian.dto';
import SignPacketDocumentSheet from './SignPacketDocumentSheet';
import type { PacketSignerOption } from './SignPacketDocumentSheet';
import UploadDocumentDialog from './UploadDocumentDialog';

export interface AdmissionPacketPanelProps {
  patientId: string;
  /** Sign in app / upload scans (documents:create and the chart is not read-only) */
  canSign: boolean;
  patientDob?: string;
  patientName?: string;
  /** A packet document was added (refresh the library list) */
  onDocumentAdded?: (doc: PatientDocument) => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : value;
};

const itemKey = (i: AdmissionPacketItem) => `${i.type}:${i.service ?? ''}`;

/** Packet guardians with the legal authority from the guardian records (read-only display) */
export const mergeSigners = (packet: AdmissionPacket | null, records: LegalGuardian[]): PacketSignerOption[] => {
  const byId = new Map(records.map((g) => [g.guardianId, g]));
  const base = packet?.guardians?.length ? packet.guardians : records;
  return base.map((g) => {
    const rec = byId.get(g.guardianId);
    return {
      guardianId: g.guardianId,
      name: g.name,
      relationship: g.relationship || rec?.relationship || '',
      legalAuthority: rec?.legalAuthority,
      canConsent: g.canConsent === true,
    };
  });
};

/**
 * Admission packet (Phase 19 F18, 26 TAC §558.282/.283/.301): the required acknowledgements and
 * consents, each signed in Pelora (drawn signature, staff witness) or uploaded as a signed scan.
 */
const AdmissionPacketPanel: React.FC<AdmissionPacketPanelProps> = ({ patientId, canSign, patientDob, patientName, onDocumentAdded }) => {
  const { user } = useAuth();
  const [packet, setPacket] = useState<AdmissionPacket | null>(null);
  const [guardianRecords, setGuardianRecords] = useState<LegalGuardian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [signing, setSigning] = useState<AdmissionPacketItem | null>(null);
  const [uploading, setUploading] = useState<AdmissionPacketItem | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, g] = await Promise.all([
        PatientDocumentsService.getAdmissionPacket(patientId),
        // Legal authority for display only; the packet already lists the guardians
        LegalGuardiansService.list(patientId).catch(() => [] as LegalGuardian[]),
      ]);
      setPacket(p);
      setGuardianRecords(g);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to load the admission packet.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { setLoading(true); void load(); }, [load]);

  const signers = useMemo(() => mergeSigners(packet, guardianRecords), [packet, guardianRecords]);
  const items = packet?.items ?? [];
  const signedCount = items.filter((i) => i.documentId).length;
  const titleFor = (i: AdmissionPacketItem) =>
    `${packet?.templates[i.type]?.title || PATIENT_DOCUMENT_TYPE_LABELS[i.type]}${i.service ? ` (${i.service})` : ''}`;
  const witnessName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'You';

  const added = (doc: PatientDocument, message: string) => {
    setNotice(message);
    onDocumentAdded?.(doc);
    // A complete packet sets the SOC consent item on the server
    notifyPatientChartUpdated(patientId);
    void load();
  };

  return (
    <section aria-label="Admission packet" className="mb-4 rounded-lg border border-slate-200 bg-white" data-testid="admission-packet">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <ClipboardSignature size={18} className="text-slate-600" />
          Admission packet
        </h3>
        {packet && (
          packet.complete
            ? <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800" data-testid="packet-status">Complete</span>
            : <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800" data-testid="packet-status">{signedCount} of {items.length} signed</span>
        )}
      </div>

      {loading && <div className="p-4"><div className="h-16 animate-pulse rounded bg-slate-100" /></div>}
      {!loading && error && (
        <div role="alert" className="m-4 flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>{error}</span>
          <button type="button" onClick={() => { setLoading(true); void load(); }} className="min-h-[44px] px-2 font-medium underline">Retry</button>
        </div>
      )}
      {notice && (
        <div role="status" className="mx-4 mt-3 flex items-center justify-between gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span className="min-w-0 break-words">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="min-h-[44px] min-w-[44px]">×</button>
        </div>
      )}

      {!loading && !error && packet && (
        <ul className="divide-y divide-slate-100">
          {items.length === 0 && <li className="px-4 py-3 text-sm text-slate-500">No packet documents are required.</li>}
          {items.map((i) => (
            <li key={itemKey(i)} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`packet-item-${itemKey(i)}`}>
              {i.documentId
                ? <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600" aria-hidden="true" />
                : <Circle className="h-5 w-5 flex-shrink-0 text-slate-400" aria-hidden="true" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{titleFor(i)}</div>
                <div className={`text-xs ${i.documentId ? 'text-slate-600' : 'text-amber-800'}`}>
                  {i.documentId
                    ? `Signed ${formatDate(i.signedAt)}${i.signerName ? ` by ${i.signerName}` : ''}`
                    : 'Missing'}
                </div>
              </div>
              {canSign && !i.documentId && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSigning(i)}
                    className="flex min-h-[44px] items-center gap-1.5 rounded-md bg-slate-800 px-3 text-sm font-medium text-white hover:bg-slate-700"
                    aria-label={`Sign now: ${titleFor(i)}`}
                  >
                    <PenLine size={16} />Sign now
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploading(i)}
                    className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50"
                    aria-label={`Upload signed scan: ${titleFor(i)}`}
                  >
                    <Upload size={16} />Upload signed scan
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <SignPacketDocumentSheet
        open={!!signing}
        patientId={patientId}
        item={signing}
        template={signing ? packet?.templates[signing.type] ?? null : null}
        guardians={signers}
        patientDob={patientDob}
        patientName={patientName}
        witnessName={witnessName}
        onClose={() => setSigning(null)}
        onSigned={(doc) => {
          const label = signing ? titleFor(signing) : doc.title;
          setSigning(null);
          added(doc, `"${label}" signed.`);
        }}
      />

      <UploadDocumentDialog
        open={!!uploading}
        patientId={patientId}
        defaultType={uploading?.type}
        defaultService={uploading?.service ?? null}
        defaultTitle={uploading ? titleFor(uploading) : ''}
        onClose={() => setUploading(null)}
        onUploaded={(doc) => {
          setUploading(null);
          added(doc, `"${doc.title}" uploaded.`);
        }}
      />
    </section>
  );
};

export default AdmissionPacketPanel;
